import { useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import {
  FormInput,
  FormItem as jsFormItem
} from 'portal/components/RecordEditing'
import {
  ClientAddressName,
  ClientKnownAddressModalLink
} from 'portal/components/RecordsModules/Edit/ClientKnownAddressModalLink'
import styled, { css } from 'styled-components'
import { Spacer } from 'portal/lib/primitives'

import { useMixpanel } from 'portal/utils/mixpanel'
import { useModuleIds } from 'portal/lib/hooks'

export const ClientKnownAddressInput = ({
  form,
  addressName,
  otherAddressName,
  knownlabel,
  knownPropertyPath,
  enteredPropertyPath,
  coordinatesPropertyPath,
  coordinatesOfOtherAddressPropertyPath,
  valueAdd = null
}: {
  form: UseFormReturn
  addressName: ClientAddressName
  otherAddressName: ClientAddressName
  knownlabel: string
  knownPropertyPath: string
  enteredPropertyPath: string
  coordinatesPropertyPath: string
  coordinatesOfOtherAddressPropertyPath: string | null
  valueAdd: ValueAddObject | null | undefined
}) => {
  const fieldValueText = form.getValues(knownPropertyPath)
  const fieldHasValue = form.watch(knownPropertyPath) != null

  const { track } = useMixpanel()
  const { moduleName, recordId } = useModuleIds() as {
    accountId: any
    projectId: any
    moduleName: any
    recordId: any
  }

  const EV_KNOWN_ADDRESS_ADD = `frontend_record_known_address_add`
  const trackAddKnownAddress = useCallback(
    newKnownAddress => {
      track(EV_KNOWN_ADDRESS_ADD, {
        record_id: recordId,
        module_name: moduleName,
        address_name: addressName,
        new_address: newKnownAddress
      })
    },
    [track, EV_KNOWN_ADDRESS_ADD, recordId, moduleName, addressName]
  )

  const EV_KNOWN_ADDRESS_EDIT = `frontend_record_known_address_edit`
  const trackUpdateKnownAddress = useCallback(
    (originalKnownAddress, updatedKnownAddress) => {
      track(EV_KNOWN_ADDRESS_EDIT, {
        record_id: recordId,
        module_name: moduleName,
        address_name: addressName,
        original_address: originalKnownAddress,
        updated_address: updatedKnownAddress
      })
    },
    [track, EV_KNOWN_ADDRESS_EDIT, recordId, moduleName, addressName]
  )

  const handleOnSubmit = useCallback(
    ({ entered, known, coordinates }) => {
      const isUpdate = fieldHasValue
      const originalAddress = {
        entered: form.getValues(enteredPropertyPath),
        known: form.getValues(knownPropertyPath),
        coordinates: form.getValues(coordinatesPropertyPath)
      }
      form.setValue(enteredPropertyPath, entered, {
        shouldValidate: true,
        shouldDirty: true
      })
      form.setValue(knownPropertyPath, known, {
        shouldValidate: true,
        shouldDirty: true
      })
      form.setValue(coordinatesPropertyPath, coordinates, {
        shouldValidate: true,
        shouldDirty: true
      })
      if (isUpdate) {
        trackUpdateKnownAddress(originalAddress, {
          entered,
          known,
          coordinates
        })
      } else {
        trackAddKnownAddress({ entered, known, coordinates })
      }
    },
    [
      fieldHasValue,
      form,
      enteredPropertyPath,
      knownPropertyPath,
      coordinatesPropertyPath,
      trackAddKnownAddress,
      trackUpdateKnownAddress
    ]
  )

  const EV_KNOWN_ADDRESS_REMOVE = `frontend_record_known_address_remove`
  const trackRemoveKnownAddress = useCallback(
    originalKnownAddress => {
      track(EV_KNOWN_ADDRESS_REMOVE, {
        record_id: recordId,
        module_name: moduleName,
        address_name: addressName,
        original_address: originalKnownAddress
      })
    },
    [track, EV_KNOWN_ADDRESS_REMOVE, recordId, moduleName, addressName]
  )

  const handleOnRemove = useCallback(() => {
    const originalAddress = {
      entered: form.getValues(enteredPropertyPath),
      known: form.getValues(knownPropertyPath),
      coordinates: form.getValues(coordinatesPropertyPath)
    }
    form.setValue(enteredPropertyPath, null, {
      shouldValidate: false,
      shouldDirty: true
    })
    form.setValue(knownPropertyPath, null, {
      shouldValidate: false,
      shouldDirty: true
    })
    form.setValue(coordinatesPropertyPath, null, {
      shouldValidate: false,
      shouldDirty: true
    })
    trackRemoveKnownAddress(originalAddress)
  }, [
    form,
    enteredPropertyPath,
    knownPropertyPath,
    coordinatesPropertyPath,
    trackRemoveKnownAddress
  ])

  type FormItemType = {
    label?: string
    valueAdd?: any
    children?: any
    hidden?: boolean
  }
  const FormItem = jsFormItem as React.FC<FormItemType>
  const otherAddressCoordinates =
    coordinatesOfOtherAddressPropertyPath !== null
      ? form.getValues(coordinatesOfOtherAddressPropertyPath)
      : null
  const currentAddressCoordinates = form.getValues(coordinatesPropertyPath)
  return (
    <>
      <FormItem label={knownlabel} valueAdd={valueAdd}>
        <KnownAddressField>
          <KnownAddressTextFiled hidden={!fieldHasValue}>
            <FormInput.Hidden name={knownPropertyPath} disabled={true} />
            <StyledFieldValue>{fieldValueText}</StyledFieldValue>
          </KnownAddressTextFiled>
          {fieldHasValue && <Spacer.Fine />}
          <ClientKnownAddressModalLink
            addressName={addressName}
            hasValue={fieldHasValue}
            onSubmit={handleOnSubmit}
            onRemove={handleOnRemove}
            otherAddressCoordinates={otherAddressCoordinates}
            currentAddressCoordinates={currentAddressCoordinates}
            otherAddressName={otherAddressName}
          />
        </KnownAddressField>
      </FormItem>

      <FormItem hidden={true}>
        <FormInput.TextArea name={coordinatesPropertyPath} disabled={true} />
      </FormItem>

      <FormItem hidden={true}>
        <FormInput.TextArea name={enteredPropertyPath} disabled={true} />
      </FormItem>
    </>
  )
}

const KnownAddressField = styled.div`
  display: flex;
`
const KnownAddressTextFiled = styled.div`
  flex-grow: 2;
  ${props =>
    props.hidden &&
    css`
      display: none;
    `}
`
const StyledFieldValue = styled.div`
  font-weight: 600;
  line-height: 2rem;
`

interface ValueAddObject {
  fieldName: string | null
  type: 'Transformation' | 'Client' | null
  transformationType: string | null
  clientUserId: string | null
  clientTimestamp: string | null
}
